/**
 * Room-card sale price for a Hubwayz offer.
 *
 * chargeIls comes from the backend quote. This module only converts that
 * ILS amount into the selected display currency. It does not add margin again.
 */

import { formatIls } from "../../packageProfileMockup/mockPackageProfileData";
import {
  HUBWAYZ_FX_CURRENCIES,
  HUBWAYZ_FX_SOURCE_FALLBACK,
  readHubwayzSupplierMoney,
} from "./resolveHubwayzHotelIlsPricing";

const KNOWN = new Set(HUBWAYZ_FX_CURRENCIES);

function upper(code) {
  const c = String(code || "").trim().toUpperCase();
  return c || "";
}

function fail(reason, extra = {}) {
  return {
    ok: false,
    reason,
    chargeIls: extra.chargeIls ?? null,
    displayAmount: null,
    displayCurrency: extra.displayCurrency ?? null,
    priced: extra.priced ?? null,
  };
}

/**
 * ILS charge → search currency. ILS is returned unchanged.
 * A missing rate or a fallback default does not produce a display price.
 */
export function convertChargeIlsToDisplayCurrency({
  chargeIls,
  displayCurrency,
  currencyRate = null,
  currencyRateSource = null,
} = {}) {
  const code = upper(displayCurrency);
  const charge = Number(chargeIls);
  if (!Number.isFinite(charge) || charge <= 0) {
    return { ok: false, reason: "invalid_amount", amount: null, currency: code || null };
  }
  if (!KNOWN.has(code)) {
    return { ok: false, reason: "unrecognized_currency", amount: null, currency: code || null };
  }
  if (code === "ILS") {
    return { ok: true, amount: charge, currency: "ILS", fxApplied: false, rateUsed: 1 };
  }
  if (currencyRateSource?.[code] === HUBWAYZ_FX_SOURCE_FALLBACK) {
    return { ok: false, reason: "invalid_fx_rate", amount: null, currency: code };
  }
  const rate = Number(currencyRate?.[code]);
  if (!Number.isFinite(rate) || rate <= 0) {
    return { ok: false, reason: "missing_fx_rate", amount: null, currency: code };
  }
  return {
    ok: true,
    amount: charge / rate,
    currency: code,
    fxApplied: true,
    rateUsed: rate,
  };
}

/**
 * Price shown from search through the order summary.
 * The selected search currency is kept. A matching server quote is shown as-is.
 * A different supplier currency is not relabeled: the server ILS charge is
 * converted once into the selected currency. Payment keeps that same charge.
 */
export function resolveHubwayzSearchCurrencyPrice({
  quote,
  displayCurrency,
  currencyRate = null,
  currencyRateSource = null,
} = {}) {
  const selected = upper(displayCurrency);
  const lockedAmount = Number(quote?.dealAmount);
  const lockedCurrency = upper(quote?.dealCurrency);
  const chargeRawEarly = Number(quote?.chargeIls ?? quote?.agentNetIls);
  const lockedCharge = Number.isFinite(chargeRawEarly) && chargeRawEarly > 0 ? chargeRawEarly : null;
  if (lockedAmount > 0 && lockedCurrency && (!selected || lockedCurrency === selected)) {
    return {
      ok: true,
      displayAmount: lockedAmount,
      displayCurrency: lockedCurrency,
      chargeIls: lockedCharge,
      fxApplied: false,
      rateUsed: null,
      source: "server_deal",
    };
  }
  const quoteCurrency = upper(quote?.agentPriceCurrency);
  const amount = Number(quote?.agentPriceAmount);
  const chargeRaw = Number(quote?.chargeIls ?? quote?.agentNetIls);
  const chargeIls = Number.isFinite(chargeRaw) && chargeRaw > 0 ? chargeRaw : null;
  if (!selected || !quoteCurrency || !(amount > 0)) {
    return {
      ok: false,
      reason: "missing_quote",
      displayAmount: null,
      displayCurrency: selected || null,
      chargeIls,
      fxApplied: false,
    };
  }
  if (quoteCurrency === selected) {
    return {
      ok: true,
      displayAmount: amount,
      displayCurrency: selected,
      chargeIls,
      fxApplied: false,
      rateUsed: 1,
    };
  }
  const display = convertChargeIlsToDisplayCurrency({
    chargeIls,
    displayCurrency: selected,
    currencyRate,
    currencyRateSource,
  });
  if (!display.ok) {
    return {
      ok: false,
      reason: display.reason,
      displayAmount: null,
      displayCurrency: selected,
      chargeIls,
      fxApplied: false,
    };
  }
  return {
    ok: true,
    displayAmount: display.amount,
    displayCurrency: display.currency,
    chargeIls,
    fxApplied: true,
    rateUsed: display.rateUsed,
  };
}

export function priceHubwayzRoomSaleDisplay(input = {}) {
  const {
    supplierAmount,
    supplierCurrency,
    displayCurrency,
    currencyRate = null,
    currencyRateSource = null,
    getHrCommissionPercent,
    getHrCommissionPercentHotels,
    hrCommissionHotelsSetting,
    getAgentCommissionPercent,
    getCreditCardFeePercent,
    chargeIls = null,
    agentCommission = null,
  } = input;
  const displayCode = upper(displayCurrency);
  void supplierAmount;
  void supplierCurrency;
  void getHrCommissionPercent;
  void getHrCommissionPercentHotels;
  void hrCommissionHotelsSetting;
  void getAgentCommissionPercent;
  void getCreditCardFeePercent;
  void agentCommission;
  let priced = null;
  const settled = Number(chargeIls);
  if (!Number.isFinite(settled) || settled <= 0) {
    return fail("server_quote_required", { displayCurrency: displayCode || null });
  }
  const display = convertChargeIlsToDisplayCurrency({
    chargeIls: settled,
    displayCurrency: displayCode,
    currencyRate,
    currencyRateSource,
  });
  if (!display.ok) {
    return fail(display.reason, {
      chargeIls: settled,
      priced,
      displayCurrency: displayCode || null,
    });
  }
  return {
    ok: true,
    reason: null,
    chargeIls: settled,
    agentNetIls: settled,
    displayAmount: display.amount,
    displayCurrency: display.currency,
    displayRate: display.rateUsed,
    priced,
  };
}

/**
 * "Starting from" price for a hotel row.
 * Each offer is priced by the existing ILS engine, then converted once into
 * the search currency. The row shows the minimum sale, not the supplier net.
 */
export function selectHubwayzMinSaleDisplay(input = {}) {
  const {
    offers,
    displayCurrency,
    currencyRate = null,
    currencyRateSource = null,
    getHrCommissionPercent,
    getHrCommissionPercentHotels,
    hrCommissionHotelsSetting,
    getAgentCommissionPercent,
    getCreditCardFeePercent,
    agentCommission = null,
  } = input;
  const list = Array.isArray(offers) ? offers : [];
  const displayCode = upper(displayCurrency);
  let best = null;
  for (const offer of list) {
    const money = readHubwayzSupplierMoney(offer);
    const serverCharge = Number(
      offer?.chargeIls ?? offer?.verifiedQuote?.chargeIls ?? offer?.agentNetIls
    );
    if (!Number.isFinite(money.amount) || money.amount <= 0 || !money.currency) continue;
    const priced = priceHubwayzRoomSaleDisplay({
      supplierAmount: money.amount,
      supplierCurrency: money.currency,
      displayCurrency: displayCode,
      currencyRate,
      currencyRateSource,
      chargeIls: serverCharge,
    });
    if (!priced.ok || !Number.isFinite(priced.displayAmount)) continue;
    const cheaper =
      !best ||
      priced.displayAmount < best.displayAmount ||
      (priced.displayAmount === best.displayAmount &&
        Number(priced.chargeIls) < Number(best.chargeIls));
    if (cheaper) best = { ...priced, offer };
  }
  if (!best) {
    return {
      ok: false,
      reason: "no_priced_offer",
      chargeIls: null,
      displayAmount: null,
      displayCurrency: displayCode || null,
      offer: null,
    };
  }
  return best;
}

/** Agent order summary. A priced Hubwayz hotel shows the ILS sale only. */
export function hubwayzAgentSummaryPriceView(pricedOk) {
  if (pricedOk) {
    return {
      label: "סה״כ נטו לסוכן",
      showSupplierAmount: false,
      showFxRate: false,
    };
  }
  return {
    label: "סה״כ להזמנה",
    showSupplierAmount: true,
    showFxRate: true,
  };
}

/** Sale amount in the search currency. Not the supplier net. */
export function formatHubwayzSaleDisplay(amount, currency) {
  const code = upper(currency);
  const n = Number(amount);
  if (!Number.isFinite(n) || !code) return "";
  if (code === "ILS") return formatIls(Math.round(n));
  const symbol = code === "USD" ? "$" : code === "EUR" ? "€" : code === "GBP" ? "£" : "";
  if (!symbol) return "";
  const formatted = n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${symbol}${formatted}`;
}
